/**
 * ProductMultiSelectField — çoklu ürün seçimi (server-side aranan picker ile).
 *
 * Düz MultiSelect tüm ürünleri DOM'a basar; binlerce üründe patlar. Bu alan
 * yerine ProductPickerModal kullanır: "Ürün Ekle" → ara → seç. Seçilenler Pill
 * olarak listelenir, ✕ ile kaldırılır.
 *
 * Değer (value) ürün id dizisidir — backend M2M alanıyla uyumlu. Seçilen
 * ürünlerin adları yerel olarak (idToName) tutulur ki picker tekrar açılmadan
 * Pill'lerde isim gösterilebilsin; düzenleme modunda dışarıdan initialNames
 * verilerek mevcut id'lerin adları doldurulur.
 */
import { useEffect, useState } from "react";
import { Button, Group, Pill, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { ProductPickerModal } from "./ProductPickerModal";

interface Props {
  /** Seçili ürün id'leri. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** id→ad — düzenleme modunda mevcut seçimlerin adlarını göstermek için. */
  initialNames?: Record<string, string>;
  label?: string;
  description?: string;
}

export function ProductMultiSelectField({
  value, onChange, initialNames, label, description,
}: Props) {
  const ids = value || [];
  const [pickerOpen, setPickerOpen] = useState(false);
  // id→ad eşlemesi: önce dışarıdan gelen adlar, sonra picker'da seçilenler.
  const [names, setNames] = useState<Record<string, string>>(initialNames || {});

  // initialNames sonradan (async) gelirse senkronla — mevcut picker seçimlerini ezmeden.
  useEffect(() => {
    if (initialNames) setNames((prev) => ({ ...initialNames, ...prev }));
  }, [initialNames]);

  function add(p: { id: string; name: string }) {
    if (ids.includes(p.id)) return;
    setNames((prev) => ({ ...prev, [p.id]: p.name }));
    onChange([...ids, p.id]);
  }

  function remove(id: string) {
    onChange(ids.filter((x) => x !== id));
  }

  return (
    <Stack gap={6}>
      {label && <Text size="sm" fw={500}>{label}</Text>}
      {description && <Text size="xs" c="dimmed">{description}</Text>}

      {ids.length > 0 ? (
        <Group gap={6}>
          {ids.map((id) => (
            <Pill key={id} withRemoveButton onRemove={() => remove(id)}>
              {names[id] || "Ürün"}
            </Pill>
          ))}
        </Group>
      ) : (
        <Text size="xs" c="dimmed">Henüz ürün seçilmedi</Text>
      )}

      <Group>
        <Button
          variant="light"
          size="compact-sm"
          leftSection={<IconPlus size={14} />}
          onClick={() => setPickerOpen(true)}
        >
          Ürün Ekle
        </Button>
      </Group>

      <ProductPickerModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={add}
        excludeIds={ids}
      />
    </Stack>
  );
}
